import React, { useRef, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { cities } from '../../data/mock';

const DestinationsSection = () => {
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-24 lg:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div
          className={`text-center mb-16 transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif text-stone-900 mb-4">
            Pick your destination
          </h2>
          <p className="text-stone-600 max-w-2xl mx-auto text-lg">
            Six of Italy's most-visited places, covered in depth
          </p>
        </div>

        {/* Destinations Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {cities.map((city, index) => (
            <Link
              key={city.id}
              to={`/city/${city.slug}`}
              className={`group block transition-all duration-700 ${
                isVisible
                  ? 'opacity-100 translate-y-0'
                  : 'opacity-0 translate-y-8'
              }`}
              style={{ transitionDelay: `${index * 100}ms` }}
            >
              <div className="relative aspect-[4/5] rounded-2xl overflow-hidden mb-4">
                <img
                  src={city.image}
                  alt={city.name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                
                {/* Content */}
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <p className="text-amber-200 text-sm font-medium mb-1">{city.tagline}</p>
                  <h3 className="text-2xl font-serif text-white mb-2">{city.name}</h3>
                  <p className="text-white/70 text-sm mb-4 line-clamp-2">{city.description}</p>
                  
                  {/* Price Badge */}
                  <div className="flex items-center justify-between">
                    <span className="text-white/60 text-sm">Full guide from</span>
                    <span className="text-white font-medium">{city.guidePrice}</span>
                  </div>
                </div>

                {/* Hover State */}
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                  <span className="px-6 py-3 bg-white text-stone-900 rounded-full font-medium flex items-center gap-2">
                    Explore
                    <ArrowRight className="w-4 h-4" />
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

export default DestinationsSection;
